import React, { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { useRouter } from 'next/router';
import { acceptNPRequest, addDocumentToProject, api, createAssignmentDocument, declineNPRequest, deleteAssignmentDocument, listProjectDocuments, projectAssignments, relatedAssignments, submitNPRequest, unsubmitNPRequest, updateFragment } from '@gtintel/platform-api';
import { useDebounce } from '@gtintel/platform-utils';
import { ReportContext } from '../ProjectApp/context';
import { requestReportTreeFrom } from '../ProjectApp/tree';
import { handleRequestError } from '../RequestApp/utils';
import { DocumentsContext, DocumentsParentContext, FlatDocumentsContext } from '../components/Documents/contexts';
import { useFlux } from '../state/useFlux';
import { uploadFileToPresignedUrl } from '../utils';
import Assignment from './Assignment';
import AssignmentList from './AssignmentList';
import { AssignmentContext, AssignmentDocumentsContext, AssignmentsContext, AssignmentsNavigationContext } from './context';
import { mutations } from './state';
const PAGE_LIMIT = 10;
const processListNPRequest = async (fetchFn, params, searchTerm, page, statuses, filterBefore, filterAfter, savedOrderTerm) => {
  const project_data = await fetchFn(params);
  const next = new URLSearchParams(String(project_data.next).split('?')[1]);
  const prev = new URLSearchParams(String(project_data.previous).split('?')[1]);
  const totalPages = Math.ceil(project_data.count / PAGE_LIMIT);
  const assignments = project_data.results.reduce((obj, item) => Object.assign(obj, {
    [String(item.id)]: item
  }), {});
  const newState = {
    assignments: {
      index: assignments
    },
    searchTerm: searchTerm ?? '',
    page: page ?? 1,
    totalPages,
    prev: {
      search: searchTerm ?? '',
      limit: prev.get('limit'),
      offset: prev.get('offset')
    },
    next: {
      search: searchTerm ?? '',
      limit: next.get('limit'),
      offset: next.get('offset')
    },
    documents: {},
    document_tree: [],
    filterStatuses: statuses,
    filterDateBefore: filterBefore,
    filterDateAfter: filterAfter,
    orderTerm: savedOrderTerm
  };
  return newState;
};
export const AssignmentsNavigator = ({
  fetchAssignments
} = {}) => {
  const router = useRouter();
  const [assignmentId, urlSection] = router.query.slug || [];
  const ctx = {
    assignmentId: () => assignmentId,
    assignmentSection: () => urlSection,
    navigateTo: assignmentId => {
      const query = {
        slug: [assignmentId]
      };
      router.push({
        pathname: router.pathname,
        query
      });
    },
    navigateToSection: section => {
      const query = {
        slug: [assignmentId, section]
      };
      router.push({
        pathname: router.pathname,
        query
      });
    },
    navigateToIndex: () => {
      router.push({
        pathname: router.pathname,
        query: {}
      });
    }
  };
  return <AssignmentsNavigationContext.Provider value={ctx}>
      <AssignmentsApp fetchAssignments={fetchAssignments} />
    </AssignmentsNavigationContext.Provider>;
};
const defaultState = {
  assignments: {
    index: {}
  },
  searchTerm: '',
  page: 1,
  totalPages: 0,
  prev: {
    search: '',
    limit: '',
    offset: ''
  },
  next: {
    search: '',
    limit: '',
    offset: ''
  },
  documents: {},
  document_tree: [],
  filterStatuses: []
};
const parseLocalStorageFilters = () => {
  const savedFilterStatuses = JSON.parse(localStorage.getItem('filterStatuses'));
  const savedOrderTerm = JSON.parse(localStorage.getItem('order'));
  const savedSearchTerm = localStorage.getItem('searchTerm');
  const savedFilterBefore = localStorage.getItem('dateBefore') ? new Date(localStorage.getItem('dateBefore')) : null;
  const savedFilterAfter = localStorage.getItem('dateAfter') ? new Date(localStorage.getItem('dateAfter')) : null;
  return {
    savedFilterStatuses,
    savedOrderTerm,
    savedSearchTerm,
    savedFilterAfter,
    savedFilterBefore
  };
};
export const AssignmentsApp = ({
  fetchAssignments
} = {}) => {
  const fetchFn = fetchAssignments || projectAssignments;
  const [state, mutate] = useFlux(defaultState);
  const debouncedUpdateFragment = useDebounce(updateFragment);
  const navigation = React.useContext(AssignmentsNavigationContext);
  const [isLoading, setIsLoading] = useState(true);
  const [css] = useStyletron();
  const buildQueryParams = (savedFilterStatuses, savedOrderTerm, savedSearchTerm, savedFilterAfter, savedFilterBefore, page) => {
    const filterStatuses = [];
    const paramsArray = [];
    if (savedFilterStatuses) {
      savedFilterStatuses.map(s => paramsArray.push(['status', s.id]));
      filterStatuses.push(...savedFilterStatuses);
    } else {
      state?.filterStatuses && state.filterStatuses.map(s => paramsArray.push(['status', s.id]));
      state?.filterStatuses && filterStatuses.push(...state.filterStatuses);
    }
    if (savedOrderTerm) {
      paramsArray.push(['ordering', savedOrderTerm.id]);
    } else {
      state?.orderTerm && paramsArray.push(['ordering', state.orderTerm.id]);
    }
    if (savedFilterBefore) {
      paramsArray.push(['updated_at_before', savedFilterBefore.toISOString()]);
    } else {
      state?.filterDateBefore && paramsArray.push(['updated_at_before', state.filterDateBefore.toISOString()]);
    }
    if (savedFilterAfter) {
      paramsArray.push(['updated_at_after', savedFilterAfter.toISOString()]);
    } else {
      state?.filterDateAfter && paramsArray.push(['updated_at_after', state.filterDateAfter.toISOString()]);
    }
    if (page) {
      paramsArray.push(...Object.entries(page));
    } else {
      paramsArray.push(['limit', `${PAGE_LIMIT}`]);
    }
    savedSearchTerm && paramsArray.push(['name', savedSearchTerm]);
    return {
      params: new URLSearchParams(paramsArray),
      filterStatuses
    };
  };
  const getAssignments = () => {
    setIsLoading(true);
    let openAssignment = null;
    if (navigation.assignmentId() && !state.assignments.index[navigation.assignmentId()]) {
      api.project.getAssignment(navigation.assignmentId()).then(assignment => {
        openAssignment = assignment.data;
      }).catch(console.error);
    } else if (navigation.assignmentId() && Object.keys(state.assignments.index).includes(navigation.assignmentId())) {
      openAssignment = state.assignments.index[navigation.assignmentId()];
    }
    const {
      savedFilterStatuses,
      savedOrderTerm,
      savedSearchTerm,
      savedFilterAfter,
      savedFilterBefore
    } = parseLocalStorageFilters();
    const {
      params,
      filterStatuses
    } = buildQueryParams(savedFilterStatuses, savedOrderTerm, savedSearchTerm, savedFilterAfter, savedFilterBefore);
    processListNPRequest(fetchFn, params.toString(), null, null, filterStatuses, savedFilterBefore, savedFilterAfter, savedOrderTerm).then(assignments => {
      if (openAssignment) {
        assignments.assignments.index[openAssignment.id] = openAssignment;
      }
      mutate(mutations.init(assignments));
    }).catch(console.error);
    setTimeout(() => setIsLoading(false), 1000);
  };
  useEffect(() => {
    getAssignments();
  }, []);
  const ctx = {
    list: () => Object.values(state.assignments.index),
    searchTerm: () => state.searchTerm,
    editSearchTerm: term => {
      mutate(mutations.editSearchTerm({
        term
      }));
    },
    filterStatuses: () => state.filterStatuses,
    setFilterStatuses: statuses => {
      mutate(mutations.setFilterStatuses({
        statuses
      }));
    },
    filterDateBefore: () => state.filterDateBefore,
    filterDateAfter: () => state.filterDateAfter,
    setFilterDateBefore: date => {
      mutate(mutations.setFilterDateBefore({
        date
      }));
    },
    setFilterDateAfter: date => {
      mutate(mutations.setFilterDateAfter({
        date
      }));
    },
    orderTerm: () => state.orderTerm,
    setOrderTerm: term => {
      mutate(mutations.setOrderTerm({
        term
      }));
    },
    currentPage: () => state.page,
    setCurrentPage: page => {
      mutate(mutations.setCurrentPage({
        page
      }));
    },
    totalPages: () => state.totalPages,
    filter: () => {
      getAssignments();
    },
    search: () => {
      const {
        savedFilterStatuses,
        savedOrderTerm,
        savedSearchTerm,
        savedFilterAfter,
        savedFilterBefore
      } = parseLocalStorageFilters();
      const {
        params,
        filterStatuses
      } = buildQueryParams(savedFilterStatuses, savedOrderTerm, savedSearchTerm, savedFilterAfter, savedFilterBefore);
      processListNPRequest(fetchFn, params.toString(), state.searchTerm, state.page, filterStatuses, savedFilterBefore, savedFilterAfter, savedOrderTerm).then(newState => {
        mutate(mutations.init(newState));
      }).catch(e => {
        console.error(e);
      });
    },
    getNext: () => {
      mutate(mutations.setCurrentPage({
        page: state.page < state.totalPages ? state.page++ : state.totalPages
      }));
      const {
        savedFilterStatuses,
        savedOrderTerm,
        savedSearchTerm,
        savedFilterAfter,
        savedFilterBefore
      } = parseLocalStorageFilters();
      const {
        params,
        filterStatuses
      } = buildQueryParams(savedFilterStatuses, savedOrderTerm, savedSearchTerm, savedFilterAfter, savedFilterBefore, state.next);
      processListNPRequest(fetchFn, params.toString(), state.searchTerm, state.page, filterStatuses, savedFilterBefore, savedFilterAfter, savedOrderTerm).then(newState => {
        mutate(mutations.init(newState));
      }).catch(e => {
        console.error(e);
      });
    },
    getPrev: () => {
      mutate(mutations.setCurrentPage({
        page: state.page > 0 ? state.page-- : 0
      }));
      const {
        savedFilterStatuses,
        savedOrderTerm,
        savedSearchTerm,
        savedFilterAfter,
        savedFilterBefore
      } = parseLocalStorageFilters();
      const {
        params,
        filterStatuses
      } = buildQueryParams(savedFilterStatuses, savedOrderTerm, savedSearchTerm, savedFilterAfter, savedFilterBefore, state.prev);
      processListNPRequest(fetchFn, params.toString(), state.searchTerm, state.page, filterStatuses, savedFilterBefore, savedFilterAfter, savedOrderTerm).then(newState => {
        mutate(mutations.init(newState));
      }).catch(e => {
        console.error(e);
      });
    }
  };
  const reportCtx = {
    report: function () {
      const a = state.assignments.index[navigation.assignmentId()];
      const s = {
        project: function () {
          return a.project;
        },
        subjects: function () {
          return a.project.subjects;
        },
        jurisdictionsFor: function (subjectId) {
          const s = a.project.subjects.find(s => s.id === subjectId);
          const jurisdictionsForSubject = s.jurisdictions.map(j => a.jurisdictions[j]);
          return jurisdictionsForSubject;
        },
        scopeComponents: function () {
          return Object.values(a.services.index);
        },
        fragments: function () {
          return a.fragments;
        }
      };
      const tree = requestReportTreeFrom(s);
      return tree;
    },
    updateFragment: async (id, body) => {
      mutate(mutations.updateFragment(navigation.assignmentId(), id, body));
      debouncedUpdateFragment(id, body);
    },
    isReadOnly: function () {
      const a = state.assignments.index[navigation.assignmentId()];
      return a.is_readonly;
    }
  };
  const assignmentDocumentsCtx = {
    documents: () => {
      const projectId = state.assignments.index[navigation.assignmentId()]?.project.id;
      if (Object.keys(state.documents).includes(projectId)) {
        return state.documents[projectId];
      } else {
        return [];
      }
    },
    documentTree: () => {
      return state.document_tree;
    },
    buildDocumentTree: documentTreeNodes => {
      mutate(mutations.documents.tree.build({
        changes: documentTreeNodes
      }));
    },
    addAttachment: async (file, type, path) => {
      const projectId = state.assignments.index[navigation.assignmentId()]?.project.id;
      const resp = await uploadFileToPresignedUrl(file, projectId, 'project', path).catch(e => {
        console.error(e);
        toaster.negative(`Could not upload ${file.name}`);
        return Promise.reject(e);
      });
      const data = {
        filename: file.name,
        path: resp.path,
        type: 'document',
        size: file.size,
        internal_path: resp.base_path
      };
      const result = await addDocumentToProject(projectId, data);
      mutate(mutations.documents.add({
        projectId,
        document: result
      }));
    },
    removeAttachment: async (documentId, type) => {
      const projectId = state.assignments.index[navigation.assignmentId()]?.project.id;
      api.attachment.delete(documentId).then(resp => {
        if (resp.status === 204) {
          mutate(mutations.documents.remove({
            projectId,
            documentId
          }));
          toaster.positive('Successfully delete a file');
        } else if (resp.status === 400) {
          toaster.negative('You do not have permission to delete this file');
        } else {
          toaster.negative('Could not delete this file');
        }
      }).catch(err => {
        console.error(err);
        toaster.negative('Could not delete this file');
      });
    },
    getAttachments: async projectId => {
      if (!projectId || projectId === 'undefined') {
        return;
      }
      // Only the lead partner can read the project's documents; every other
      // partner gets the files shared with their own assignment.
      const assignment = state.assignments.index[navigation.assignmentId()];
      const documents = assignment?.is_lead_partner_assignment ? await listProjectDocuments(projectId).catch(handleRequestError) : assignment?.shared_documents ?? [];
      mutate(mutations.documents.init({
        documents: {
          [projectId]: documents || []
        }
      }));
    }
  };
  const assignmentCtx = {
    status: () => state.assignments.index[navigation.assignmentId()]?.status,
    getRelatedAssignments: () => {
      relatedAssignments(navigation.assignmentId()).then(data => {
        mutate(mutations.assignments.edit(navigation.assignmentId(), {
          related_assignments: data.related
        }));
      }).catch(e => {
        console.error(e);
      });
    },
    getRelatedAssignment: () => state.relatedAssignmentId,
    setRelatedAssignment: id => {
      mutate(mutations.selectRelatedAssignment(id));
    },
    dueDate: () => state.assignments.index[navigation.assignmentId()]?.due_date ?? 'Not set',
    acceptedDate: () => state.assignments.index[navigation.assignmentId()]?.accepted_date,
    activatedDate: () => state.assignments.index[navigation.assignmentId()]?.assigned_date,
    submittedDate: () => state.assignments.index[navigation.assignmentId()]?.submitted_date,
    finishedDate: () => state.assignments.index[navigation.assignmentId()]?.finished_date,
    investigatorNotes: () => state.assignments.index[navigation.assignmentId()]?.investigator_notes ?? '',
    fee: () => state.assignments.index[navigation.assignmentId()]?.fee,
    currency: () => state.assignments.index[navigation.assignmentId()]?.project.currency,
    accept: async () => {
      const id = navigation.assignmentId();
      const npRequestModel = await acceptNPRequest(id);
      mutate(mutations.updateAssignmentStatus(id, npRequestModel.status));
    },
    decline: async () => {
      const id = navigation.assignmentId();
      const npRequestModel = await declineNPRequest(id);
      mutate(mutations.updateAssignmentStatus(id, npRequestModel.status));
    },
    submit: async notes => {
      const id = navigation.assignmentId();
      const npRequestModel = await submitNPRequest(id, notes);
      mutate(mutations.updateAssignmentStatus(id, npRequestModel.status));
      mutate(mutations.setAssignmentReadOnly(id, true));
      // Echo the notes into the local index so the readonly view shows
      // them immediately — the fetched entry predates this submission.
      mutate(mutations.assignments.edit(id, {
        investigator_notes: notes ?? ''
      }));
    },
    unsubmit: async () => {
      const id = navigation.assignmentId();
      const npRequestModel = await unsubmitNPRequest(id);
      mutate(mutations.updateAssignmentStatus(id, npRequestModel.status));
      mutate(mutations.setAssignmentReadOnly(id, false));
    },
    name: () => state.assignments.index[navigation.assignmentId()]?.name,
    projectId: () => String(state.assignments.index[navigation.assignmentId()]?.project.id),
    documents: () => {
      return state.assignments.index[navigation.assignmentId()]?.documents;
    },
    addAttachment: async (file, type, path) => {
      const resp = await uploadFileToPresignedUrl(file, state.relatedAssignmentId ?? navigation.assignmentId(), 'assignment', path).catch(e => {
        console.error(e);
        toaster.negative(`Could not upload ${file.name}`);
        return Promise.reject(e);
      });
      const data = {
        filename: file.name,
        path: resp.path,
        type: 'document',
        size: file.size,
        internal_path: resp.base_path
      };
      const doc = await createAssignmentDocument(state.relatedAssignmentId ?? navigation.assignmentId(), data);
      mutate(mutations.assignments.documents.add(navigation.assignmentId(), doc));
    },
    removeAttachment: async (id, type) => {
      await deleteAssignmentDocument(state.relatedAssignmentId ?? navigation.assignmentId(), id);
      mutate(mutations.assignments.documents.remove(navigation.assignmentId(), id));
    },
    subjects: () => state.assignments.index[navigation.assignmentId()]?.project.subjects,
    isLeadPartnerAssignment: () => state.assignments.index[navigation.assignmentId()].is_lead_partner_assignment,
    isQaAssignment: () => state.assignments.index[navigation.assignmentId()]?.is_qa_assignment,
    relatedAssignments: () => state.assignments.index[navigation.assignmentId()]?.related_assignments,
    sampleReport: () => state.assignments.index[navigation.assignmentId()]?.sample_report,
    reportTemplate: () => state.assignments.index[navigation.assignmentId()]?.report_template
  };
  if (Object.values(state.assignments.index).length === 0 && isLoading || isLoading) {
    return <section className={css({
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      height: 'calc(100vh - 168px)'
    })}>
        <Spinner />
      </section>;
  }
  const view = navigation.assignmentId() === undefined ? <AssignmentList /> : <AssignmentContext.Provider value={assignmentCtx}>
        <AssignmentDocumentsContext.Provider value={assignmentDocumentsCtx}>
          <FlatDocumentsContext.Provider value={assignmentCtx}>
            <ReportContext.Provider value={reportCtx}>
              <DocumentsParentContext.Provider value={assignmentCtx}>
                <DocumentsContext.Provider value={assignmentDocumentsCtx}>
                  <Assignment />
                </DocumentsContext.Provider>
              </DocumentsParentContext.Provider>
            </ReportContext.Provider>
          </FlatDocumentsContext.Provider>
        </AssignmentDocumentsContext.Provider>
      </AssignmentContext.Provider>;
  return <AssignmentsContext.Provider value={ctx}>
      {view}
    </AssignmentsContext.Provider>;
};